import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, setDoc, addDoc, updateDoc, getDocs,
  collection, query, where, limit, serverTimestamp, Timestamp, writeBatch,
} from 'firebase/firestore';
import {
  getAuth, signOut as fbSignOut, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, sendEmailVerification,
  sendPasswordResetEmail, RecaptchaVerifier, PhoneAuthProvider,
  linkWithCredential, GoogleAuthProvider, signInWithPopup, signInWithRedirect,
  getRedirectResult, signInWithCredential,
} from 'firebase/auth';
import { writeLog, sha256 } from '../../utils/firebaseUtils';
import { validateInviteCode, redeemInviteCode } from '../../utils/inviteUtils';

let _db   = null;
let _auth = null;
const DB   = () => { if (!_db)   { try { _db   = getFirestore(getApp()); } catch(_) {} } return _db; };
const AUTH = () => { if (!_auth) { try { _auth = getAuth(getApp());      } catch(_) {} } return _auth; };

// ── Device fingerprint ────────────────────────────────────────────────────────
async function deviceFingerprint() {
  try {
    const w   = window.screen.width;
    const h   = window.screen.height;
    const raw = `${navigator.userAgent}|${w}|${h}|${navigator.language}`;
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('').slice(0,20);
  } catch(_) { return 'unknown'; }
}

// ── Login security helpers ────────────────────────────────────────────────────
async function checkLockout(db, email) {
  try {
    const hash = (await sha256(email)).slice(0,16);
    const snap = await getDoc(doc(db, 'login_lockouts', hash));
    if (!snap.exists()) return null;
    const data = snap.data();
    if (!data.lockedUntil) return null;
    const until = data.lockedUntil.toDate();
    if (until > new Date()) return { until, remaining: Math.ceil((until - Date.now()) / 60000) };
    return null;
  } catch(_) { return null; }
}

async function recordFailedLogin(db, email) {
  try {
    const hash  = (await sha256(email)).slice(0,16);
    const ref   = doc(db, 'login_lockouts', hash);
    const snap  = await getDoc(ref);
    const count = (snap.exists() ? snap.data().failedCount || 0 : 0) + 1;
    const lockedUntil = count >= 5
      ? Timestamp.fromDate(new Date(Date.now() + 30 * 60000))
      : null;
    await setDoc(ref, { email, failedCount: count, lastFailedAt: serverTimestamp(), lockedUntil }, { merge: true });
    return { count, lockedUntil };
  } catch(_) { return { count: 1, lockedUntil: null }; }
}

async function clearLockout(db, email) {
  try {
    const hash = (await sha256(email)).slice(0,16);
    await setDoc(doc(db, 'login_lockouts', hash), {
      email, failedCount: 0, lockedUntil: null, lastSuccessAt: serverTimestamp(),
    }, { merge: true });
  } catch(_) {}
}

async function trackDevice(db, uid, email, fp) {
  if (fp === 'unknown') return;
  const ref  = doc(db, 'authorized_users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const known = snap.data().knownDevices || [];
  if (known.includes(fp)) return;

  await updateDoc(ref, { knownDevices: [...known, fp] });
  // Notify user
  await addDoc(collection(db, 'notifications'), {
    type: 'new_device', recipientId: uid,
    title: 'New Device Sign-in',
    message: `New device detected for ${email}. If this wasn't you, contact admin immediately.`,
    createdAt: serverTimestamp(), read: false,
  });
  // Notify admins
  const adminSnap = await getDocs(query(
    collection(db, 'authorized_users'),
    where('role', '==', 'admin'), where('active', '==', true)
  ));
  for (const aDoc of adminSnap.docs) {
    if (aDoc.id === uid) continue;
    await addDoc(collection(db, 'notifications'), {
      type: 'new_device', recipientId: aDoc.id,
      title: 'New Device Alert',
      message: `New device login detected for ${email}`,
      createdAt: serverTimestamp(), read: false,
    });
  }
}

// ── Portal cards for guided registration ─────────────────────────────────────
const PORTAL_CARDS_REG = [
  { id:'mp',           icon:'🛰️',  label:'Mission Planning',     desc:'Mission trajectory, CONOPS, scheduling',      color:'#6366f1' },
  { id:'embedded',     icon:'🔧',  label:'Embedded Systems',     desc:'ADCS firmware, sensors, microcontrollers',    color:'#f59e0b' },
  { id:'system',       icon:'⚙️',  label:'Systems Engineering',  desc:'Integration, verification, requirements',     color:'#10b981' },
  { id:'ops',          icon:'🎛️',  label:'Operations Officer',   desc:'Ground ops, telemetry, command & control',    color:'#e11d48', dualApproval: true },
  { id:'pe',           icon:'🤖',  label:'Prompt Engineering',   desc:'AI/ML pipelines, language model integration', color:'#8b5cf6' },
  { id:'experimental', icon:'🔬',  label:'Experimental Results', desc:'Data analysis, experiment validation',        color:'#06b6d4' },
  { id:'finance',      icon:'💰',  label:'Finance & Admin',      desc:'Payroll, compliance, administration',         color:'#34d399', dualApproval: true },
  { id:'mentor',       icon:'👤',  label:'Mentor / Guest',       desc:'Advisory, external collaboration',            color:'#a855f7' },
  { id:'obc',          icon:'💻',  label:'OBC Development',      desc:'On-board computer, RTOS, software',          color:'#00FFB2' },
];

// Popups are blocked in embedded browsers (e.g. Cursor design preview) — use email sign-in instead.
export function isEmbeddedPreviewBrowser() {
  if (typeof window === 'undefined') return false;
  if (window.self !== window.top) return true;
  const ua = navigator.userAgent || '';
  return /Electron|Cursor/i.test(ua);
}

function makeGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: 'spacemancraft.in' });
  return provider;
}

// ── AuthGate (top-level login shell) ─────────────────────────────────────────
export default function AuthGate({ onSuccess, forceBootstrap, onBack }) {
  const db   = DB();
  const auth = AUTH();
  const [view,              setView]             = useState(
    forceBootstrap ? 'bootstrap' : (isEmbeddedPreviewBrowser() ? 'email' : 'google')
  );
  const [bootstrapNeeded,   setBootstrapNeeded]  = useState(forceBootstrap || false);
  const [googleBusy,        setGoogleBusy]       = useState(false);
  const [googleErr,         setGoogleErr]        = useState('');
  const [googlePendingUser, setGooglePendingUser] = useState(null);
  const [inviteStatus,      setInviteStatus]     = useState(null);

  useEffect(() => {
    if (forceBootstrap) { setBootstrapNeeded(true); return; }
    if (!db) return;
    getDoc(doc(db, 'app_config', 'bootstrap')).then(snap => {
      if (!snap.exists() || !snap.data().done) setBootstrapNeeded(true);
    }).catch(() => setBootstrapNeeded(true));
  }, [forceBootstrap, db]);

  // Detect ?invite= URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inv = params.get('invite');
    if (!inv) return;
    const code = inv.toUpperCase().trim();
    validateInviteCode(code).then(result => {
      setInviteStatus(result);
      if (result.valid) setView('register');
    }).catch(() => setInviteStatus({ valid: false, error: 'Could not validate invite link' }));
  }, []);

  async function processGoogleUser(user) {
    const db   = DB();
    const auth = AUTH();
    if (!db || !auth) return;

    const email  = user.email || '';
    const isOrg  = email.toLowerCase().endsWith('@spacemancraft.in');

    const auSnap = await getDoc(doc(db, 'authorized_users', user.uid));
    if (auSnap.exists()) {
      const ud = auSnap.data();
      if (ud.active === false) { await fbSignOut(auth); setGoogleErr('Account disabled. Contact admin.'); setGoogleBusy(false); return; }
      if (ud.suspended === true) { await fbSignOut(auth); setGoogleErr('Your account has been suspended. Please contact your administrator.'); setGoogleBusy(false); return; }
      if (ud.expiresAt?.toDate() < new Date()) {
        await fbSignOut(auth);
        setGoogleErr(`Your access expired on ${ud.expiresAt.toDate().toLocaleDateString('en-IN')}. Contact admin to renew.`);
        setGoogleBusy(false); return;
      }
      try { await updateDoc(doc(db, 'authorized_users', user.uid), { lastActive: serverTimestamp() }); } catch(_) {}
      await writeLog('login_google', { email, role: ud.role });
      sessionStorage.setItem('sat_admin_session', ud.role === 'admin' ? '1' : '');
      const fp = await deviceFingerprint();
      await trackDevice(db, user.uid, email, fp).catch(() => {});
      onSuccess({ role: ud.role, portals: ud.portals, name: ud.name || user.displayName || email });
      return;
    }

    if (!isOrg) {
      await fbSignOut(auth);
      setGoogleErr('Only @spacemancraft.in Google accounts are allowed. External users must submit an access request.');
      setGoogleBusy(false); return;
    }

    const bsSnap = await getDoc(doc(db, 'app_config', 'bootstrap'));
    if (!bsSnap.exists() || !bsSnap.data().done) {
      sessionStorage.setItem('_gbs_uid',   user.uid);
      sessionStorage.setItem('_gbs_email', email);
      sessionStorage.setItem('_gbs_name',  user.displayName || email.split('@')[0]);
      setBootstrapNeeded(true);
      setView('bootstrap');
      setGoogleBusy(false); return;
    }

    // Pre-approved members ("Add Member" in a portal) are provisioned by the
    // autoActivatePreApproved Cloud Function moments after the Auth account is
    // created. Poll briefly for the profile before falling back to the
    // request-access flow. (Accounts created before that function existed are
    // fixed by scripts/backfill-preapproved.mjs.)
    for (let i = 0; i < 6; i++) {
      const s = await getDoc(doc(db, 'authorized_users', user.uid));
      if (s.exists()) {
        const ud2 = s.data();
        if (ud2.active === false) {
          await fbSignOut(auth);
          setGoogleErr('Account disabled. Contact admin.'); setGoogleBusy(false); return;
        }
        try { await updateDoc(doc(db, 'authorized_users', user.uid), { lastActive: serverTimestamp() }); } catch(_) {}
        await writeLog('login_google', { email, role: ud2.role });
        sessionStorage.setItem('sat_admin_session', ud2.role === 'admin' ? '1' : '');
        const fp2 = await deviceFingerprint();
        await trackDevice(db, user.uid, email, fp2).catch(() => {});
        onSuccess({ role: ud2.role, portals: ud2.portals, name: ud2.name || user.displayName || email });
        return;
      }
      await new Promise(r => setTimeout(r, 1200));
    }

    let existingReqStatus = null;
    const reqByUid = await getDocs(query(
      collection(db, 'access_requests'),
      where('uid', '==', user.uid), limit(1)
    ));
    if (!reqByUid.empty) {
      existingReqStatus = reqByUid.docs[0].data().status;
    } else {
      // Legacy fallback: a request keyed by doc-ID == uid. A non-existent doc
      // here can surface as permission-denied under the access_requests rule,
      // so this must never abort sign-in.
      try {
        const legacySnap = await getDoc(doc(db, 'access_requests', user.uid));
        if (legacySnap.exists()) existingReqStatus = legacySnap.data().status;
      } catch (_) { /* no legacy request — fall through to request-access */ }
    }

    if (existingReqStatus) {
      setGoogleBusy(false);
      if (existingReqStatus === 'pending' || existingReqStatus === 'info_requested' || existingReqStatus === 'info_responded') {
        await fbSignOut(auth);
        setGooglePendingUser({ uid: user.uid, email, name: user.displayName || email.split('@')[0] });
        setView('google_pending');
      } else if (existingReqStatus === 'denied' || existingReqStatus === 'rejected') {
        await fbSignOut(auth);
        setGoogleErr('Your access request was denied. Contact admin.');
      } else {
        await fbSignOut(auth);
        setGoogleErr('Account not yet activated. Contact admin.');
      }
      return;
    }

    setGooglePendingUser({ uid: user.uid, email, name: user.displayName || email.split('@')[0] });
    setView('google_request');
    setGoogleBusy(false);
  }

  // Complete Google redirect sign-in when returning from Google (embedded browsers).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const authInst = AUTH();
      const dbInst   = DB();
      if (!authInst || !dbInst) return;
      try {
        const result = await getRedirectResult(authInst);
        if (!result?.user || cancelled) return;
        setGoogleBusy(true);
        setGoogleErr('');
        await processGoogleUser(result.user);
      } catch (ex) {
        if (!cancelled) {
          setGoogleErr(ex.message || 'Google sign-in failed.');
          setGoogleBusy(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleGoogle() {
    const auth = AUTH();
    if (!DB() || !auth) return;
    if (isEmbeddedPreviewBrowser()) {
      setGoogleErr('Google sign-in is blocked in the Cursor preview browser. Use email sign-in below, or open http://localhost:3000 in Chrome for Google login.');
      setView('email');
      return;
    }
    // Google refuses OAuth inside app WebViews (Android APK) — steer to email sign-in
    if (window.Capacitor?.isNativePlatform?.()) {
      setGoogleErr('Google sign-in isn\'t available inside the Android app yet. Please use email sign-in below.');
      setView('email');
      return;
    }
    setGoogleBusy(true); setGoogleErr('');
    try {
      const provider = makeGoogleProvider();
      const result = await signInWithPopup(auth, provider);
      await processGoogleUser(result.user);
    } catch (ex) {
      if (ex.code === 'auth/popup-closed-by-user' || ex.code === 'auth/cancelled-popup-request') {
        setGoogleBusy(false); return;
      }
      if (ex.code === 'auth/popup-blocked') {
        try {
          await signInWithRedirect(auth, makeGoogleProvider());
          return;
        } catch (redirectEx) {
          setGoogleErr('Popup blocked. Use email sign-in below, or open this app in Chrome for Google login.');
          setView('email');
          setGoogleBusy(false);
          return;
        }
      }
      setGoogleErr(ex.message || 'Google sign-in failed.');
      setGoogleBusy(false);
    }
  }

  return (
    <div className="smc-auth-shell" style={{ minHeight:'100vh', background:'#070a0f', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ width:'100%', maxWidth:400 }}>
        {/* Logo Banner */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <img src="/logo-dark.png" alt="Spacemancraft"
            style={{ width:180, height:180, objectFit:'contain', borderRadius:16,
              boxShadow:'0 0 40px #0e9cc030, 0 0 80px #0e9cc010',
              marginBottom:16, display:'block', margin:'0 auto 16px' }} />
          <div style={{ fontFamily:'monospace', fontSize:9, color:'#0e9cc066', letterSpacing:4 }}>
            SECURE ACCESS PORTAL
          </div>
        </div>

        {(view === 'google' || view === 'email') && (
          <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28 }}>
            {isEmbeddedPreviewBrowser() && (
              <div style={{ fontFamily:'monospace', fontSize:9, color:'#0e9cc0', background:'#0e9cc010',
                border:'1px solid #0e9cc033', padding:'10px 12px', marginBottom:16, lineHeight:1.7, borderRadius:6 }}>
                Cursor preview browser — use <strong>email sign-in</strong> below.
                For Google login, open <strong>http://localhost:3000</strong> in Chrome.
              </div>
            )}
            <button onClick={handleGoogle} disabled={googleBusy}
              style={{ width:'100%', padding:'13px 16px', fontFamily:'monospace', fontSize:12, letterSpacing:1,
                background: googleBusy ? '#111820' : '#0e1525', border:'1px solid #1e3a5f',
                color: googleBusy ? '#445566' : '#7baee8', cursor: googleBusy ? 'wait' : 'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:12,
                borderRadius:8, marginBottom:8, transition:'all 0.15s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {googleBusy ? 'SIGNING IN…' : 'SIGN IN WITH GOOGLE'}
            </button>
            <div style={{ fontFamily:'monospace', fontSize:9, color:'#2a3a50', textAlign:'center', marginBottom:20 }}>
              For @spacemancraft.in accounts
            </div>

            {googleErr && (
              <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', background:'#f43f5e10',
                border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:16, borderRadius:6 }}>
                {googleErr}
              </div>
            )}

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ flex:1, height:1, background:'#1a2030' }} />
              <span style={{ fontFamily:'monospace', fontSize:9, color:'#2a3a50' }}>or</span>
              <div style={{ flex:1, height:1, background:'#1a2030' }} />
            </div>

            {view === 'google' ? (
              <button onClick={() => setView('email')}
                style={{ width:'100%', padding:'10px', fontFamily:'monospace', fontSize:10, letterSpacing:1,
                  background:'transparent', border:'1px solid #1e2636', color:'#445566',
                  cursor:'pointer', borderRadius:6 }}>
                SIGN IN WITH EMAIL →
              </button>
            ) : (
              <SignInForm onSuccess={onSuccess} onBack={() => setView('google')} />
            )}

            {view === 'google' && (
              <button onClick={() => setView('phone')}
                style={{ width:'100%', padding:'10px', fontFamily:'monospace', fontSize:10, letterSpacing:1,
                  background:'transparent', border:'1px solid #1e2636', color:'#445566',
                  cursor:'pointer', borderRadius:6, marginTop:8 }}>
                📱 SIGN IN WITH PHONE OTP
              </button>
            )}

            <div style={{ display:'flex', justifyContent:'space-between', marginTop:20, flexWrap:'wrap', gap:8 }}>
              <button onClick={() => setView('register')}
                style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace',
                  fontSize:9, cursor:'pointer', letterSpacing:1, padding:0 }}>
                REQUEST ACCESS
              </button>
              {bootstrapNeeded && (
                <button onClick={() => setView('bootstrap')}
                  style={{ background:'none', border:'none', color:'#334455', fontFamily:'monospace',
                    fontSize:9, cursor:'pointer', letterSpacing:1, padding:0 }}>
                  ADMIN SETUP
                </button>
              )}
              {onBack && (
                <button onClick={onBack}
                  style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace',
                    fontSize:9, cursor:'pointer', letterSpacing:1, padding:0 }}>
                  ← PUBLIC PAGE
                </button>
              )}
            </div>
          </div>
        )}

        {view === 'phone' && (
          <PhoneSignIn onSuccess={onSuccess} onBack={() => setView('google')} />
        )}
        {view === 'google_request' && googlePendingUser && (
          <GoogleDeptRequestForm
            user={googlePendingUser}
            onBack={() => { setGooglePendingUser(null); setView('google'); }}
            onSubmitted={() => setView('google_pending')}
          />
        )}
        {view === 'google_pending' && (
          <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:16 }}>⏳</div>
            <div style={{ fontFamily:'monospace', fontSize:13, color:'#10b981', marginBottom:10 }}>Request Submitted</div>
            <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', lineHeight:1.7 }}>
              Your department access request is pending admin approval.<br/>
              You'll be able to sign in once it's approved.
            </div>
            <button onClick={() => { setGooglePendingUser(null); setView('google'); }}
              style={{ marginTop:20, background:'none', border:'none', color:'#445566',
                fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1 }}>
              ← BACK TO SIGN IN
            </button>
          </div>
        )}
        {view === 'register' && (
          <div>
            <button onClick={() => setView('google')} style={{ background:'none', border:'none', color:'#445566',
              fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1, padding:'0 0 16px', display:'block' }}>
              ← BACK
            </button>
            {inviteStatus?.valid && (
              <div style={{ padding:'10px 14px', marginBottom:12, background:'rgba(16,185,129,0.08)',
                border:'1px solid rgba(16,185,129,0.3)', borderRadius:6,
                fontFamily:'monospace', fontSize:9, color:'#10b981', lineHeight:1.6 }}>
                🎟 Invite detected — role <strong>{inviteStatus.data?.role}</strong> pre-assigned.
                Your account will be activated immediately after registration.
              </div>
            )}
            {inviteStatus && !inviteStatus.valid && (
              <div style={{ padding:'10px 14px', marginBottom:12, background:'rgba(244,63,94,0.08)',
                border:'1px solid rgba(244,63,94,0.3)', borderRadius:6,
                fontFamily:'monospace', fontSize:9, color:'#f43f5e' }}>
                ⚠ {inviteStatus.error}
              </div>
            )}
            <GuidedRegisterForm
              inviteStatus={inviteStatus?.valid ? inviteStatus : null}
              onSwitchToSignIn={() => setView('google')}
              onSuccess={onSuccess}
            />
          </div>
        )}
        {view === 'bootstrap' && bootstrapNeeded && (
          <div>
            <button onClick={() => setView('google')} style={{ background:'none', border:'none', color:'#445566',
              fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1, padding:'0 0 16px', display:'block' }}>
              ← BACK
            </button>
            <BootstrapForm onSuccess={(r) => { onSuccess(r); setBootstrapNeeded(false); }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── GoogleDeptRequestForm ─────────────────────────────────────────────────────
function GoogleDeptRequestForm({ user, onBack, onSubmitted }) {
  const DEPTS = [
    { id:'mp',       label:'Mission Planning',    color:'#6366f1' },
    { id:'embedded', label:'Embedded Systems',    color:'#f59e0b' },
    { id:'system',   label:'Systems Engineering', color:'#10b981' },
    { id:'ops',      label:'Operations Officer',  color:'#e11d48' },
  ];
  const [dept,   setDept]   = useState('mp');
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!reason.trim()) { setErr('Please describe your role and why you need access'); return; }
    const db   = DB();
    const auth = AUTH();
    if (!db || !auth) { setErr('Service unavailable'); return; }
    setBusy(true); setErr('');
    try {
      const deptLabel = DEPTS.find(d => d.id === dept)?.label || dept;
      await addDoc(collection(db, 'access_requests'), {
        uid: user.uid, name: user.name, email: user.email,
        role: dept, roleLabel: deptLabel, team: dept, reason: reason.trim(),
        status: 'pending', loginMethod: 'google',
        submittedAt: serverTimestamp(), requestedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'notifications_queue'), {
        eventType: 'ACCESS_REQUEST_SUBMITTED',
        actorUid: user.uid, actorName: user.name,
        payload: {
          applicantName: user.name, applicantEmail: user.email,
          applicantUid: user.uid, requestedRole: dept, roleLabel: deptLabel,
        },
        processed: false, createdAt: serverTimestamp(),
      });
      try {
        const adminsSnap = await getDocs(query(collection(db, 'authorized_users'), where('role', '==', 'admin'), where('active', '==', true)));
        const nb = writeBatch(db);
        adminsSnap.docs.forEach(ad => {
          nb.set(doc(collection(db, 'notifications')), {
            recipientId: ad.id, type: 'ACCESS_REQUEST_SUBMITTED',
            title: `New Access Request: ${user.name}`,
            body: `${user.email} is requesting ${deptLabel} access`,
            navTarget: 'requests', read: false, createdAt: serverTimestamp(),
          });
        });
        await nb.commit();
      } catch (_) {}
      await writeLog('access_request', { email: user.email, team: dept });
      await fbSignOut(auth);
      onSubmitted();
    } catch(ex) { setErr(ex.message || 'Submission failed. Please try again.'); }
    finally { setBusy(false); }
  }

  const selectedDept = DEPTS.find(d => d.id === dept);

  return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28 }}>
      <button type="button" onClick={onBack} style={{ background:'none', border:'none', color:'#445566',
        fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1, padding:'0 0 16px', display:'block' }}>
        ← BACK
      </button>
      <div style={{ fontFamily:'monospace', fontSize:11, color:'#0e9cc0', letterSpacing:2, marginBottom:4 }}>
        REQUEST DEPARTMENT ACCESS
      </div>
      <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', marginBottom:20 }}>
        Signed in as <span style={{ color:'#7baee8' }}>{user.email}</span>
      </div>
      <form onSubmit={submit}>
        <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:6 }}>
          YOUR DEPARTMENT
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
          {DEPTS.map(d => (
            <label key={d.id} style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer',
              padding:'10px 14px', borderRadius:6,
              background: dept === d.id ? `${d.color}15` : '#070a0f',
              border: `1px solid ${dept === d.id ? d.color : '#1e2636'}`,
              transition:'all 0.15s' }}>
              <input type="radio" name="dept" value={d.id} checked={dept === d.id}
                onChange={() => setDept(d.id)} style={{ accentColor: d.color }} />
              <span style={{ fontFamily:'monospace', fontSize:11, color: dept === d.id ? d.color : '#7b8fa8' }}>
                {d.label}
              </span>
            </label>
          ))}
        </div>
        <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:6 }}>
          YOUR ROLE & REASON FOR ACCESS
        </div>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder={`Describe your role in ${selectedDept?.label} and what you'll use this for`}
          style={{ width:'100%', background:'#070a0f', border:'1px solid #1e2636', color:'#dde9f0',
            padding:'10px 14px', fontFamily:'monospace', fontSize:11, marginBottom:12,
            resize:'vertical', boxSizing:'border-box', borderRadius:4 }} />
        {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e',
          background:'#f43f5e10', border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:12, borderRadius:4 }}>
          {err}
        </div>}
        <button type="submit" disabled={busy}
          style={{ width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11, letterSpacing:2,
            background: busy ? '#1e2636' : `${selectedDept?.color}20`,
            border:`1px solid ${busy ? '#2a3a50' : selectedDept?.color}`,
            color: busy ? '#445566' : selectedDept?.color,
            cursor: busy ? 'wait' : 'pointer', borderRadius:6 }}>
          {busy ? 'SUBMITTING…' : 'SUBMIT ACCESS REQUEST →'}
        </button>
      </form>
    </div>
  );
}

// ── SignInForm ────────────────────────────────────────────────────────────────
function SignInForm({ onSuccess, onBack }) {
  const [email,         setEmail]         = useState('');
  const [pw,            setPw]            = useState('');
  const [busy,          setBusy]          = useState(false);
  const [err,           setErr]           = useState('');
  const [shake,         setShake]         = useState(false);
  const [reset,         setReset]         = useState(false);
  const [expiryWarning, setExpiryWarning] = useState(null);
  const [infoRequest,   setInfoRequest]   = useState(null); // { reqDocId, uid, question }
  const [infoResponse,  setInfoResponse]  = useState('');
  const [infoSubmitDone, setInfoSubmitDone] = useState(false);

  function doShake(msg) { setErr(msg); setShake(true); setTimeout(() => setShake(false), 600); setBusy(false); }

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !pw) { doShake('Email and password required'); return; }
    const db   = DB();
    const auth = AUTH();
    setBusy(true); setErr('');

    // Check lockout before attempting auth
    const lockInfo = await checkLockout(db, email.trim());
    if (lockInfo) {
      doShake(`Too many failed attempts. Try again in ${lockInfo.remaining} minute${lockInfo.remaining !== 1 ? 's' : ''}.`);
      return;
    }

    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pw);
      const uid  = cred.user.uid;

      let auSnap = await getDoc(doc(db, 'authorized_users', uid));
      if (!auSnap.exists()) {
        // Pre-approved members are provisioned by the autoActivatePreApproved
        // Cloud Function right after the Auth account is created. Poll briefly
        // for the profile before falling back to the request-access flow.
        for (let i = 0; i < 6 && !auSnap.exists(); i++) {
          await new Promise(r => setTimeout(r, 1200));
          auSnap = await getDoc(doc(db, 'authorized_users', uid));
        }
      }
      if (!auSnap.exists()) {
        // Client-side pre-approval fast-path (best effort — the Cloud Function
        // above is authoritative; these reads can be denied for a user not yet
        // in authorized_users, and must never abort sign-in).
        try {
          const preApprovedQuery = await getDocs(query(
            collection(db, 'access_requests'),
            where('preApprovedEmail', '==', cred.user.email.toLowerCase()),
            where('status', '==', 'pre_approved')
          ));
          if (!preApprovedQuery.empty) {
            const preReq = preApprovedQuery.docs[0].data();
            await setDoc(doc(db, 'authorized_users', uid), {
              uid, name: preReq.name, email: cred.user.email,
              role: preReq.role, portals: preReq.portals || [preReq.role], active: true,
              approvedAt: serverTimestamp(), approvedBy: preReq.approvedBy || 'Admin',
            });
            await updateDoc(preApprovedQuery.docs[0].ref, { status: 'approved', uid });
            await clearLockout(db, email.trim());
            sessionStorage.setItem('sat_admin_session', preReq.role === 'admin' ? '1' : '');
            onSuccess({ role: preReq.role, portals: preReq.portals, name: preReq.name });
            return;
          }
        } catch (_) { /* fall through to request-access flow */ }
        // Look up by uid field (new schema) then by doc ID (legacy)
        let reqData = null; let reqDocId = null;
        const reqByUid = await getDocs(query(
          collection(db, 'access_requests'),
          where('uid', '==', uid), limit(1)
        ));
        if (!reqByUid.empty) {
          reqData = reqByUid.docs[0].data();
          reqDocId = reqByUid.docs[0].id;
        } else {
          try {
            const legacySnap = await getDoc(doc(db, 'access_requests', uid));
            if (legacySnap.exists()) { reqData = legacySnap.data(); reqDocId = legacySnap.id; }
          } catch (_) { /* no legacy request */ }
        }

        if (reqData) {
          const status = reqData.status;
          if (status === 'pending') {
            doShake('Your request is pending admin approval.');
            await fbSignOut(auth);
          } else if (status === 'info_requested') {
            // Keep user signed in — show inline response form
            setBusy(false);
            setInfoRequest({
              reqDocId,
              uid,
              question: reqData.reviewNote || reqData.infoQuestion || 'Please provide additional information.',
            });
          } else if (status === 'info_responded') {
            doShake('Your information has been received. Awaiting admin review.');
            await fbSignOut(auth);
          } else if (status === 'denied' || status === 'rejected') {
            const note = reqData.reviewNote ? ` Reason: ${reqData.reviewNote}` : '';
            doShake(`Access request was not approved.${note} Contact admin.`);
            await fbSignOut(auth);
          } else {
            doShake('Account not yet activated. Contact admin.');
            await fbSignOut(auth);
          }
        } else {
          doShake('No access request found. Please request access first.');
          await fbSignOut(auth);
        }
        return;
      }

      const userData = auSnap.data();
      if (userData.active === false) { doShake('Account disabled. Contact admin.'); await fbSignOut(auth); return; }
      if (userData.suspended === true) { doShake('Your account has been suspended. Please contact your administrator.'); await fbSignOut(auth); return; }

      // Expiry check
      if (userData.expiresAt?.toDate) {
        const expDate  = userData.expiresAt.toDate();
        const daysLeft = Math.ceil((expDate - Date.now()) / 86400000);
        if (daysLeft < 0) {
          doShake(`Your access expired on ${expDate.toLocaleDateString('en-IN')}. Contact admin to renew.`);
          await fbSignOut(auth); return;
        }
        if (daysLeft <= 14) {
          // Show warning interstitial; user can continue
          await clearLockout(db, email.trim());
          const expiryUpdate = { lastActive: serverTimestamp() };
          if (userData.active !== true) expiryUpdate.active = true;
          await updateDoc(doc(db, 'authorized_users', uid), expiryUpdate).catch(() => {});
          await writeLog('login', { email: cred.user.email, role: userData.role });
          sessionStorage.setItem('sat_admin_session', userData.role === 'admin' ? '1' : '');
          const fp = await deviceFingerprint();
          await trackDevice(db, uid, cred.user.email, fp).catch(() => {});
          setExpiryWarning({
            daysLeft,
            dateStr: expDate.toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' }),
            pendingSuccess: { role: userData.role, name: userData.name || email },
          });
          setBusy(false);
          return;
        }
      }

      const loginUpdate = { lastActive: serverTimestamp() };
      if (userData.active !== true) loginUpdate.active = true; // backfill missing active field
      try { await updateDoc(doc(db, 'authorized_users', uid), loginUpdate); } catch(_) {}

      // Non-blocking: auto-close any open attendance session from yesterday
      (() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        const yDate = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        const yRef  = doc(db, 'attendance', uid, 'records', yDate);
        getDoc(yRef).then(ySnap => {
          if (!ySnap.exists() || ySnap.data().isOpen !== true) return;
          const checkedInAt = ySnap.data().checkedInAt?.toDate?.();
          if (!checkedInAt) return;
          const eodIST  = new Date(yDate + 'T18:29:00Z'); // 23:59 IST
          const duration = Math.max(0, Math.round((eodIST - checkedInAt) / 60000));
          return updateDoc(yRef, {
            checkoutTime:     Timestamp.fromDate(eodIST),
            checkoutMethod:   'auto-login',
            duration_minutes: duration,
            isOpen:           false,
          });
        }).catch(() => {});
      })();

      await writeLog('login', { email: cred.user.email, role: userData.role });
      await clearLockout(db, email.trim());
      sessionStorage.setItem('sat_admin_session', userData.role === 'admin' ? '1' : '');
      const fp = await deviceFingerprint();
      await trackDevice(db, uid, cred.user.email, fp).catch(() => {});
      onSuccess({ role: userData.role, portals: userData.portals, name: userData.name || email });
    } catch(ex) {
      if (ex.code === 'auth/invalid-credential' || ex.code === 'auth/wrong-password' || ex.code === 'auth/user-not-found') {
        const { count, lockedUntil } = await recordFailedLogin(db, email.trim());
        if (lockedUntil) {
          doShake(`Too many failed attempts. Account locked for 30 minutes.`);
        } else {
          const remaining = 5 - count;
          doShake(`Invalid email or password.${remaining > 0 ? ` ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.` : ''}`);
        }
      } else if (ex.message && !ex.code) { doShake(ex.message); }
      else { doShake('Sign in failed. Check your credentials.'); }
    }
  }

  async function resetPassword() {
    if (!email.trim()) { doShake('Enter your email address first'); return; }
    const auth = AUTH();
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setReset(true);
    } catch(ex) { doShake('Could not send reset email.'); }
  }

  async function submitInfoResponse() {
    if (!infoResponse.trim()) { setErr('Please provide a response.'); return; }
    const db   = DB();
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      await updateDoc(doc(db, 'access_requests', infoRequest.reqDocId), {
        status:          'info_responded',
        infoResponse:    infoResponse.trim(),
        infoRespondedAt: serverTimestamp(),
      });
      await fbSignOut(auth);
      setInfoSubmitDone(true);
    } catch(ex) {
      setErr(ex.message || 'Failed to submit. Please try again.');
    } finally { setBusy(false); }
  }

  // Info submit done screen
  if (infoSubmitDone) return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28, textAlign:'center' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
      <div style={{ fontFamily:'monospace', fontSize:13, color:'#10b981', marginBottom:8 }}>Response Submitted</div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', lineHeight:1.6, marginBottom:20 }}>
        Admin will review your response and get back to you.
      </div>
      <button onClick={() => { setInfoSubmitDone(false); setInfoRequest(null); setInfoResponse(''); }}
        style={{ background:'none', border:'none', color:'#0e9cc0', fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1 }}>
        ← BACK TO SIGN IN
      </button>
    </div>
  );

  // Info requested screen
  if (infoRequest) return (
    <div style={{ background:'#0c0f18', border:'1px solid #8b5cf644', borderRadius:12, padding:28 }}>
      <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b5cf6', letterSpacing:2, marginBottom:16 }}>
        ❓ ADMIN NEEDS MORE INFORMATION
      </div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', marginBottom:6, letterSpacing:1 }}>
        QUESTION FROM ADMIN
      </div>
      <div style={{
        background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.25)',
        borderRadius:6, padding:'10px 14px', fontFamily:'monospace', fontSize:11,
        color:'#7b8fa8', lineHeight:1.6, marginBottom:16,
      }}>
        {infoRequest.question}
      </div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', marginBottom:5, letterSpacing:1 }}>
        YOUR RESPONSE *
      </div>
      <textarea
        value={infoResponse}
        onChange={e => setInfoResponse(e.target.value)}
        rows={4}
        placeholder="Provide the requested information…"
        style={{
          width:'100%', background:'#0b0e14', border:'1px solid #1e2636',
          color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12,
          resize:'none', lineHeight:1.5, marginBottom:8, boxSizing:'border-box',
        }}
      />
      {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', marginBottom:8 }}>{err}</div>}
      <button onClick={submitInfoResponse} disabled={busy}
        style={{
          width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11, letterSpacing:1,
          background: busy ? '#1e2636' : '#8b5cf620', border:`1px solid ${busy ? '#1e2636' : '#8b5cf6'}`,
          color: busy ? '#445566' : '#8b5cf6', cursor: busy ? 'wait' : 'pointer', marginBottom:10,
        }}>
        {busy ? 'SUBMITTING…' : 'SUBMIT RESPONSE →'}
      </button>
      <button onClick={async () => { try { await fbSignOut(AUTH()); } catch(_) {} setInfoRequest(null); }}
        style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace', fontSize:9, cursor:'pointer' }}>
        ← BACK TO SIGN IN
      </button>
    </div>
  );

  // Expiry warning interstitial
  if (expiryWarning) return (
    <div style={{ background:'#0c0f18', border:'1px solid #f59e0b44', borderRadius:8, padding:28, textAlign:'center' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>⚠️</div>
      <div style={{ fontFamily:'monospace', fontSize:13, color:'#f59e0b', marginBottom:8 }}>Access Expiring Soon</div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#7b8fa8', lineHeight:1.8, marginBottom:20 }}>
        Your access expires in <strong style={{ color:'#f59e0b' }}>{expiryWarning.daysLeft} day{expiryWarning.daysLeft !== 1 ? 's' : ''}</strong>
        <br />({expiryWarning.dateStr})
        <br />Contact your admin to renew.
      </div>
      <button onClick={() => onSuccess(expiryWarning.pendingSuccess)}
        style={{ width:'100%', padding:'11px', fontFamily:'monospace', fontSize:10, letterSpacing:1,
          background:'#f59e0b20', border:'1px solid #f59e0b', color:'#f59e0b', cursor:'pointer', marginBottom:10 }}>
        CONTINUE →
      </button>
      <button onClick={() => setExpiryWarning(null)}
        style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace', fontSize:9, cursor:'pointer' }}>
        ← BACK
      </button>
    </div>
  );

  return (
    <form onSubmit={submit} style={{ animation: shake ? 'shake 0.4s' : 'none' }}>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-8px)}75%{transform:translateX(8px)}}`}</style>
      {onBack && (
        <button type="button" onClick={onBack} style={{ background:'none', border:'none', color:'#445566',
          fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1, padding:'0 0 12px', display:'block' }}>
          ← BACK
        </button>
      )}
      <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email address"
        style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
          padding:'11px 14px', fontFamily:'monospace', fontSize:13, marginBottom:10, boxSizing:'border-box' }} />
      <input value={pw} onChange={e => setPw(e.target.value)} type="password" placeholder="Password"
        style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
          padding:'11px 14px', fontFamily:'monospace', fontSize:13, marginBottom:16, boxSizing:'border-box' }} />
      {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', background:'#f43f5e10', border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:12 }}>{err}</div>}
      {reset && <div style={{ fontFamily:'monospace', fontSize:10, color:'#10b981', background:'#10b98110', border:'1px solid #10b98133', padding:'8px 12px', marginBottom:12 }}>Password reset email sent. Check your inbox.</div>}
      <button type="submit" disabled={busy} style={{
        width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11, letterSpacing:2,
        background: busy ? '#1e2636' : '#0e9cc020', border:`1px solid ${busy ? '#1e2636' : '#0e9cc0'}`,
        color: busy ? '#445566' : '#0e9cc0', cursor: busy ? 'wait' : 'pointer', marginBottom:10 }}>
        {busy ? 'SIGNING IN…' : 'SIGN IN →'}
      </button>
      <button type="button" onClick={resetPassword}
        style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace', fontSize:9,
          cursor:'pointer', letterSpacing:1, padding:0 }}>
        Forgot password?
      </button>
    </form>
  );
}

// ── GuidedRegisterForm — 3-step registration ──────────────────────────────────
function GuidedRegisterForm({ inviteStatus, onSwitchToSignIn, onSuccess }) {
  const [step,          setStep]          = useState(1);
  const [name,          setName]          = useState('');
  const [email,         setEmail]         = useState('');
  const [pw,            setPw]            = useState('');
  const [phone,         setPhone]         = useState('');
  const [otp,           setOtp]           = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [confRes,       setConfRes]       = useState(null);
  const [team,          setTeam]          = useState(inviteStatus?.role || 'mp');
  const [reason,        setReason]        = useState('');
  const [referral,      setReferral]      = useState('');
  const [linkedin,      setLinkedin]      = useState('');
  const [busy,          setBusy]          = useState(false);
  const [err,           setErr]           = useState('');
  const [otpSent,       setOtpSent]       = useState(false);
  const [done,          setDone]          = useState(false);

  const hasInvite = !!inviteStatus;

  async function sendOTP() {
    if (!name.trim() || !email.trim() || !pw || pw.length < 6) {
      setErr('Fill in name, email, and password (min 6 chars) first'); return;
    }
    if (!phone.trim()) { setErr('Enter your phone number with country code'); return; }
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      let user = auth.currentUser;
      if (!user) {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), pw);
        user = cred.user;
        await sendEmailVerification(user);
      }
      if (!window._recaptchaVerifier) {
        window._recaptchaVerifier = new RecaptchaVerifier(auth, 'guided-recaptcha', {
          size: 'invisible', callback: () => {},
        });
      }
      const phoneProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneProvider.verifyPhoneNumber(phone.trim(), window._recaptchaVerifier);
      setConfRes(verificationId);
      setOtpSent(true);
      setErr('');
    } catch(ex) {
      setErr(ex.message || 'Could not send OTP. Check phone number format (+91XXXXXXXXXX).');
      if (window._recaptchaVerifier) { window._recaptchaVerifier.clear(); window._recaptchaVerifier = null; }
    } finally { setBusy(false); }
  }

  async function verifyOTP() {
    if (!otp.trim() || otp.length < 6) { setErr('Enter the 6-digit OTP'); return; }
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      const credential = PhoneAuthProvider.credential(confRes, otp.trim());
      await linkWithCredential(auth.currentUser, credential);
      setPhoneVerified(true);
      setOtpSent(false);
      setErr('');
    } catch(ex) {
      setErr(ex.code === 'auth/invalid-verification-code' ? 'Incorrect OTP. Try again.' : (ex.message || 'OTP verification failed.'));
    } finally { setBusy(false); }
  }

  async function submit() {
    if (!reason.trim()) { setErr('Please describe your reason for access'); return; }
    const db   = DB();
    const auth = AUTH();
    if (!db || !auth) { setErr('Service unavailable'); return; }
    setBusy(true); setErr('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated. Please retry.');

      if (hasInvite) {
        // Invite code redemption — direct activation
        await redeemInviteCode({ code: inviteStatus.code, uid: user.uid, name: name.trim(), email: email.trim() });
        await writeLog('register_invite', { email: email.trim(), role: inviteStatus.role, code: inviteStatus.code });
        sessionStorage.setItem('sat_admin_session', inviteStatus.role === 'admin' ? '1' : '');
        const fp = await deviceFingerprint();
        await trackDevice(db, user.uid, email.trim(), fp).catch(() => {});
        onSuccess({ role: inviteStatus.role, name: name.trim() });
      } else {
        // Standard access request — use addDoc (auto-ID) with uid as field
        const roleLabel = PORTAL_CARDS_REG.find(p => p.id === team)?.label || team;
        await addDoc(collection(db, 'access_requests'), {
          uid: user.uid, name: name.trim(), email: email.trim(),
          phone: phone.trim() || null,
          role: team, roleLabel, team,
          reason: reason.trim(),
          referral: referral.trim() || null,
          linkedinUrl: linkedin.trim() || null,
          linkedin: linkedin.trim() || null,
          status: 'pending', phoneVerified,
          emailVerified: user.emailVerified,
          submittedAt: serverTimestamp(),
          requestedAt: serverTimestamp(),
          reviewedBy: null, reviewedByUid: null, reviewedAt: null,
          reviewNote: null, infoResponse: null, infoRespondedAt: null,
          notifiedApplicant: false,
        });
        await addDoc(collection(db, 'notifications_queue'), {
          eventType: 'ACCESS_REQUEST_SUBMITTED',
          actorUid: user.uid, actorName: name.trim(),
          payload: {
            applicantName: name.trim(), applicantEmail: email.trim(),
            applicantUid: user.uid, requestedRole: team, roleLabel,
          },
          processed: false, createdAt: serverTimestamp(),
        });
        try {
          const adminsSnap = await getDocs(query(collection(db, 'authorized_users'), where('role', '==', 'admin'), where('active', '==', true)));
          const nb = writeBatch(db);
          adminsSnap.docs.forEach(ad => {
            nb.set(doc(collection(db, 'notifications')), {
              recipientId: ad.id, type: 'ACCESS_REQUEST_SUBMITTED',
              title: `New Access Request: ${name.trim()}`,
              body: `${email.trim()} is requesting ${roleLabel} access`,
              navTarget: 'requests', read: false, createdAt: serverTimestamp(),
            });
          });
          await nb.commit();
        } catch (_) {}
        await writeLog('access_request', { email: email.trim(), team });
        await fbSignOut(auth);
        setDone(true);
      }
    } catch(ex) { setErr(ex.message || 'Submission failed.'); }
    finally { setBusy(false); }
  }

  if (done) return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28, textAlign:'center' }}>
      <div style={{ fontSize:40, marginBottom:16 }}>✅</div>
      <div style={{ fontFamily:'monospace', fontSize:13, color:'#dde9f0', marginBottom:8 }}>Request Submitted</div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', marginBottom:24, lineHeight:1.7 }}>
        Check your email to verify your address.<br/>
        Admin will review your request and activate your account.
      </div>
      <button onClick={onSwitchToSignIn} style={{ fontFamily:'monospace', fontSize:9, color:'#0e9cc0', background:'#0e9cc015', border:'1px solid #0e9cc055', padding:'8px 20px', cursor:'pointer' }}>
        ← BACK TO SIGN IN
      </button>
    </div>
  );

  const stepLabel = hasInvite
    ? ['CREDENTIALS', 'CONFIRM ROLE', 'DETAILS']
    : ['CREDENTIALS', 'SELECT PORTAL', 'DETAILS'];

  return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28 }}>
      <div id="guided-recaptcha" />

      {/* Step indicator */}
      <div style={{ display:'flex', gap:6, marginBottom:20, alignItems:'center' }}>
        {[1,2,3].map(s => (
          <div key={s} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{
              width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:'monospace', fontSize:9, fontWeight:700,
              background: step > s ? '#10b981' : step === s ? '#0e9cc020' : 'transparent',
              border: `1px solid ${step > s ? '#10b981' : step === s ? '#0e9cc0' : '#1e2636'}`,
              color: step > s ? '#070a0f' : step === s ? '#0e9cc0' : '#334455',
            }}>
              {step > s ? '✓' : s}
            </div>
            <span style={{ fontFamily:'monospace', fontSize:8, color: step === s ? '#0e9cc0' : '#334455', letterSpacing:1 }}>
              {stepLabel[s-1]}
            </span>
            {s < 3 && <div style={{ flex:1, height:1, background:'#1a2030', width:20 }} />}
          </div>
        ))}
      </div>

      {err && (
        <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', background:'#f43f5e10',
          border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:14, borderRadius:4 }}>
          {err}
        </div>
      )}

      {/* ── Step 1: Credentials ── */}
      {step === 1 && (
        <div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
            style={inputSt} />
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Work email address"
            style={inputSt} />
          <input value={pw} onChange={e => setPw(e.target.value)} type="password" placeholder="Password (min 6 chars)"
            style={inputSt} />
          {!hasInvite && (
            <>
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 XXXXXXXXXX"
                  style={{ flex:1, background:'#0b0e14', border:`1px solid ${phoneVerified ? '#10b981' : '#1e2636'}`,
                    color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, boxSizing:'border-box' }} />
                {phoneVerified
                  ? <div style={{ padding:'10px 12px', fontFamily:'monospace', fontSize:10, color:'#10b981',
                      border:'1px solid #10b98155', background:'#10b98110', whiteSpace:'nowrap' }}>✓ VERIFIED</div>
                  : <button type="button" onClick={sendOTP} disabled={busy}
                      style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:9,
                        background:'#0e9cc015', border:'1px solid #0e9cc055',
                        color:'#0e9cc0', cursor: busy ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                      {busy ? '…' : 'SEND OTP'}
                    </button>
                }
              </div>
              {otpSent && !phoneVerified && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', marginBottom:6 }}>
                    OTP sent to {phone}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} placeholder="6-digit OTP"
                      style={{ flex:1, background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
                        padding:'10px 14px', fontFamily:'monospace', fontSize:16, letterSpacing:6,
                        textAlign:'center', boxSizing:'border-box' }} />
                    <button type="button" onClick={verifyOTP} disabled={busy}
                      style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:9, whiteSpace:'nowrap',
                        background:'#10b98120', border:'1px solid #10b98155',
                        color:'#10b981', cursor: busy ? 'wait' : 'pointer' }}>
                      {busy ? '…' : 'VERIFY'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          <button onClick={() => {
            if (!name.trim() || !email.trim() || !pw || pw.length < 6) {
              setErr('Fill in all fields. Password must be at least 6 characters.'); return;
            }
            if (!hasInvite && !phoneVerified) { setErr('Please verify your phone number first'); return; }
            setErr(''); setStep(2);
          }}
            style={{ width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11, letterSpacing:1,
              background: (hasInvite || phoneVerified) ? '#0e9cc020' : 'transparent',
              border:`1px solid ${(hasInvite || phoneVerified) ? '#0e9cc0' : '#1e2636'}`,
              color: (hasInvite || phoneVerified) ? '#0e9cc0' : '#445566',
              cursor: (hasInvite || phoneVerified) ? 'pointer' : 'default', marginTop:4 }}>
            NEXT →
          </button>
        </div>
      )}

      {/* ── Step 2: Portal selection ── */}
      {step === 2 && (
        <div>
          {hasInvite ? (
            <div>
              <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', marginBottom:12 }}>
                Your role has been pre-assigned by the invite link:
              </div>
              {(() => {
                const card = PORTAL_CARDS_REG.find(p => p.id === inviteStatus.role);
                return card ? (
                  <div style={{ background:`${card.color}15`, border:`1px solid ${card.color}44`,
                    borderRadius:8, padding:'14px 16px', display:'flex', gap:12, alignItems:'center' }}>
                    <span style={{ fontSize:24 }}>{card.icon}</span>
                    <div>
                      <div style={{ fontFamily:'monospace', fontSize:12, color:card.color, fontWeight:700 }}>{card.label}</div>
                      <div style={{ fontFamily:'monospace', fontSize:10, color:'#7b8fa8', marginTop:3 }}>{card.desc}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontFamily:'monospace', fontSize:11, color:'#0e9cc0' }}>{inviteStatus.role}</div>
                );
              })()}
            </div>
          ) : (
            <div>
              <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:12 }}>
                SELECT YOUR PORTAL
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {PORTAL_CARDS_REG.map(card => (
                  <button key={card.id} type="button"
                    onClick={() => setTeam(card.id)}
                    style={{ background: team === card.id ? `${card.color}15` : '#070a0f',
                      border:`1px solid ${team === card.id ? card.color : '#1e2636'}`,
                      borderRadius:6, padding:'10px 12px', cursor:'pointer', textAlign:'left',
                      transition:'all 0.15s', position:'relative' }}>
                    {card.dualApproval && (
                      <span style={{ position:'absolute', top:4, right:6, fontFamily:'monospace', fontSize:7,
                        color:'#f59e0b', background:'#f59e0b15', padding:'1px 5px', borderRadius:3 }}>
                        DUAL APPROVAL
                      </span>
                    )}
                    <div style={{ fontSize:18, marginBottom:4 }}>{card.icon}</div>
                    <div style={{ fontFamily:'monospace', fontSize:10, color: team === card.id ? card.color : '#7b8fa8',
                      fontWeight:600, marginBottom:2 }}>
                      {card.label}
                    </div>
                    <div style={{ fontFamily:'monospace', fontSize:8, color:'#334455', lineHeight:1.4 }}>
                      {card.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:8, marginTop:16 }}>
            <button onClick={() => { setErr(''); setStep(1); }}
              style={{ flex:1, padding:'10px', fontFamily:'monospace', fontSize:10,
                background:'transparent', border:'1px solid #1e2636', color:'#445566', cursor:'pointer' }}>
              ← BACK
            </button>
            <button onClick={() => { setErr(''); setStep(3); }}
              style={{ flex:2, padding:'10px', fontFamily:'monospace', fontSize:10, letterSpacing:1,
                background:'#0e9cc020', border:'1px solid #0e9cc0', color:'#0e9cc0', cursor:'pointer' }}>
              NEXT →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Details & submit ── */}
      {step === 3 && (
        <div>
          <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:5 }}>
            WHY DO YOU NEED ACCESS? *
          </div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder={`Describe your role and what you'll use this system for`}
            style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
              padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:10,
              resize:'vertical', boxSizing:'border-box' }} />
          <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:5 }}>
            HOW DID YOU HEAR ABOUT US?
          </div>
          <input value={referral} onChange={e => setReferral(e.target.value)}
            placeholder="Referral / source (optional)"
            style={{ ...inputSt, marginBottom:8 }} />
          <div style={{ fontFamily:'monospace', fontSize:9, color:'#445566', letterSpacing:1, marginBottom:5 }}>
            LINKEDIN PROFILE
          </div>
          <input value={linkedin} onChange={e => setLinkedin(e.target.value)}
            placeholder="https://linkedin.com/in/… (optional)"
            style={{ ...inputSt, marginBottom:14 }} />

          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { setErr(''); setStep(2); }}
              style={{ flex:1, padding:'10px', fontFamily:'monospace', fontSize:10,
                background:'transparent', border:'1px solid #1e2636', color:'#445566', cursor:'pointer' }}>
              ← BACK
            </button>
            <button onClick={submit} disabled={busy}
              style={{ flex:2, padding:'11px', fontFamily:'monospace', fontSize:11, letterSpacing:1,
                background: busy ? '#1e2636' : hasInvite ? '#10b98120' : '#6366f120',
                border:`1px solid ${busy ? '#1e2636' : hasInvite ? '#10b981' : '#6366f1'}`,
                color: busy ? '#445566' : hasInvite ? '#10b981' : '#6366f1',
                cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'SUBMITTING…' : hasInvite ? 'ACTIVATE ACCOUNT →' : 'SUBMIT REQUEST →'}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop:20, textAlign:'center' }}>
        <button onClick={onSwitchToSignIn}
          style={{ background:'none', border:'none', color:'#334455', fontFamily:'monospace', fontSize:9, cursor:'pointer' }}>
          Already have access? Sign In
        </button>
      </div>
    </div>
  );
}

const inputSt = {
  width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
  padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:8, boxSizing:'border-box',
};

// ── BootstrapForm ─────────────────────────────────────────────────────────────
function BootstrapForm({ onSuccess }) {
  const googleUid   = sessionStorage.getItem('_gbs_uid')   || '';
  const googleEmail = sessionStorage.getItem('_gbs_email') || '';
  const googleName  = sessionStorage.getItem('_gbs_name')  || '';
  const isGoogleClaim = !!googleUid;

  const [name,    setName]    = useState(googleName);
  const [email,   setEmail]   = useState(googleEmail);
  const [pw,      setPw]      = useState('');
  const [pw2,     setPw2]     = useState('');
  const [adminPw, setAdminPw] = useState('');
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !adminPw) { setErr('All fields required'); return; }
    if (!isGoogleClaim && (!email.trim() || !pw)) { setErr('All fields required'); return; }
    const db   = DB();
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      const hash = await sha256(adminPw);
      const cfgSnap = await getDoc(doc(db, 'app_config', 'auth'));
      if (!cfgSnap.exists() || cfgSnap.data().admin_hash !== hash) {
        setErr('Incorrect admin bootstrap password.'); setBusy(false); return;
      }
      if (isGoogleClaim) {
        const nameVal = name.trim();
        await setDoc(doc(db, 'authorized_users', googleUid), {
          uid: googleUid, name: nameVal, email: googleEmail,
          role: 'admin', active: true,
          approvedAt: serverTimestamp(), approvedBy: 'bootstrap_google',
        });
        await setDoc(doc(db, 'app_config', 'bootstrap'), { done: true, bootstrappedAt: serverTimestamp() });
        sessionStorage.removeItem('_gbs_uid');
        sessionStorage.removeItem('_gbs_email');
        sessionStorage.removeItem('_gbs_name');
        sessionStorage.setItem('sat_admin_session', '1');
        onSuccess({ role: 'admin', name: nameVal });
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), pw);
      const user = cred.user;
      await sendEmailVerification(user);
      await setDoc(doc(db, 'authorized_users', user.uid), {
        uid: user.uid, name: name.trim(), email: email.trim(),
        role: 'admin', active: true,
        approvedAt: serverTimestamp(), approvedBy: 'bootstrap',
      });
      await setDoc(doc(db, 'app_config', 'bootstrap'), { done: true, at: serverTimestamp() });
      sessionStorage.setItem('sat_admin_session', '1');
      onSuccess({ role: 'admin', name: name.trim() });
    } catch(ex) {
      if (ex.code === 'auth/email-already-in-use') setErr('Email already registered. Use Sign In.');
      else setErr(ex.message || 'Bootstrap failed.');
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit}>
      <div style={{ fontFamily:'monospace', fontSize:9, color:'#f59e0b', background:'#f59e0b10', border:'1px solid #f59e0b33', padding:'8px 12px', marginBottom:16, lineHeight:1.6 }}>
        FIRST-TIME ADMIN SETUP — Enter the existing admin password to claim admin access.
      </div>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
        style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:8, boxSizing:'border-box' }} />
      {!isGoogleClaim && (
        <>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Your email address"
            style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:8, boxSizing:'border-box' }} />
          <input value={pw} onChange={e => setPw(e.target.value)} type="password" placeholder="Choose a strong password"
            style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:8, boxSizing:'border-box' }} />
          <input value={pw2} onChange={e => setPw2(e.target.value)} type="password" placeholder="Confirm password"
            style={{ width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:8, boxSizing:'border-box' }} />
        </>
      )}
      {isGoogleClaim && (
        <div style={{ fontFamily:'monospace', fontSize:11, color:'#10b981', background:'#10b98110',
          border:'1px solid #10b98133', padding:'10px 14px', marginBottom:16, borderRadius:4 }}>
          Signed in as {googleEmail} via Google. Enter admin key to claim admin role.
        </div>
      )}
      <input value={adminPw} onChange={e => setAdminPw(e.target.value)} type="password" placeholder="Existing admin password (orbit)"
        style={{ width:'100%', background:'#0b0e14', border:'1px solid #f59e0b44', color:'#dde9f0', padding:'10px 14px', fontFamily:'monospace', fontSize:12, marginBottom:12, boxSizing:'border-box' }} />
      {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', marginBottom:12 }}>{err}</div>}
      <button type="submit" disabled={busy} style={{
        width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11, letterSpacing:2,
        background: busy ? '#1e2636' : '#f59e0b20', border:`1px solid ${busy ? '#1e2636' : '#f59e0b'}`,
        color: busy ? '#445566' : '#f59e0b', cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'SETTING UP…' : 'CLAIM ADMIN ACCESS →'}
      </button>
    </form>
  );
}

// ── PhoneSignIn ───────────────────────────────────────────────────────────────
function PhoneSignIn({ onSuccess, onBack }) {
  const [phone,   setPhone]   = useState('');
  const [otp,     setOtp]     = useState('');
  const [step,    setStep]    = useState('phone');
  const [confRes, setConfRes] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  async function sendOTP() {
    if (!phone.trim()) { setErr('Enter phone number with country code e.g. +91 9876543210'); return; }
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      if (!window.recaptchaVerifierPhone) {
        window.recaptchaVerifierPhone = new RecaptchaVerifier(auth, 'phone-recaptcha-phone', { size: 'invisible' });
      }
      const cr = await PhoneAuthProvider.verifyPhoneNumber(phone.trim(), window.recaptchaVerifierPhone);
      setConfRes(cr);
      setStep('otp');
    } catch (ex) {
      setErr(ex.message || 'Failed to send OTP. Check phone number.');
    }
    setBusy(false);
  }

  async function verifyOTP() {
    if (!otp.trim()) { setErr('Enter the 6-digit OTP'); return; }
    const db   = DB();
    const auth = AUTH();
    setBusy(true); setErr('');
    try {
      const cred   = PhoneAuthProvider.credential(confRes, otp.trim());
      const result = await signInWithCredential(auth, cred);
      const user   = result.user;
      const auSnap = await getDoc(doc(db, 'authorized_users', user.uid));
      if (auSnap.exists()) {
        const ud = auSnap.data();
        if (ud.active === false) { await fbSignOut(auth); setErr('Account disabled.'); setBusy(false); return; }
        if (ud.suspended === true) { await fbSignOut(auth); setErr('Your account has been suspended. Please contact your administrator.'); setBusy(false); return; }
        if (ud.expiresAt?.toDate() < new Date()) {
          await fbSignOut(auth);
          setErr(`Your access expired on ${ud.expiresAt.toDate().toLocaleDateString('en-IN')}. Contact admin.`);
          setBusy(false); return;
        }
        try { await updateDoc(doc(db, 'authorized_users', user.uid), { lastActive: serverTimestamp() }); } catch(_) {}
        await writeLog('login_phone', { phone, role: ud.role });
        sessionStorage.setItem('sat_admin_session', ud.role === 'admin' ? '1' : '');
        const fp = await deviceFingerprint();
        await trackDevice(db, user.uid, phone, fp).catch(() => {});
        onSuccess({ role: ud.role, portals: ud.portals, name: ud.name || phone });
        return;
      }
      // Query by uid field (new schema) + legacy doc-ID fallback
      let phoneReqData = null;
      const phoneReqByUid = await getDocs(query(
        collection(db, 'access_requests'), where('uid', '==', user.uid), limit(1)
      ));
      if (!phoneReqByUid.empty) {
        phoneReqData = phoneReqByUid.docs[0].data();
      } else {
        try {
          const legacySnap = await getDoc(doc(db, 'access_requests', user.uid));
          if (legacySnap.exists()) phoneReqData = legacySnap.data();
        } catch (_) { /* no legacy request */ }
      }
      if (phoneReqData) {
        const st = phoneReqData.status;
        if (st === 'pending' || st === 'info_requested' || st === 'info_responded') {
          setErr('Your request is pending admin approval.'); await fbSignOut(auth); setBusy(false); return;
        }
        if (st === 'denied' || st === 'rejected') {
          setErr('Access request was denied. Contact admin.'); await fbSignOut(auth); setBusy(false); return;
        }
      }
      // First time phone login — create access request
      await addDoc(collection(db, 'access_requests'), {
        uid: user.uid, name: phone, phone: phone.trim(),
        email: '', role: 'unknown', roleLabel: 'Unknown', team: 'unknown',
        reason: 'Phone OTP login', status: 'pending', phoneVerified: true,
        submittedAt: serverTimestamp(), requestedAt: serverTimestamp(),
        reviewedBy: null, reviewedByUid: null, reviewedAt: null,
        reviewNote: null, infoResponse: null, notifiedApplicant: false,
      });
      await addDoc(collection(db, 'notifications_queue'), {
        eventType: 'ACCESS_REQUEST_SUBMITTED',
        actorUid: user.uid, actorName: phone,
        payload: {
          applicantName: phone, applicantEmail: '', applicantUid: user.uid,
          requestedRole: 'unknown', roleLabel: 'Phone OTP',
        },
        processed: false, createdAt: serverTimestamp(),
      });
      try {
        const adminsSnap = await getDocs(query(collection(db, 'authorized_users'), where('role', '==', 'admin'), where('active', '==', true)));
        const nb = writeBatch(db);
        adminsSnap.docs.forEach(ad => {
          nb.set(doc(collection(db, 'notifications')), {
            recipientId: ad.id, type: 'ACCESS_REQUEST_SUBMITTED',
            title: 'New Access Request: Phone OTP',
            body: `${phone} is requesting access via phone OTP`,
            navTarget: 'requests', read: false, createdAt: serverTimestamp(),
          });
        });
        await nb.commit();
      } catch (_) {}
      await fbSignOut(auth);
      setStep('pending');
    } catch (ex) {
      setErr(ex.code === 'auth/invalid-verification-code' ? 'Incorrect OTP. Try again.' : ex.message || 'Verification failed.');
    }
    setBusy(false);
  }

  const inputStyle = { width:'100%', background:'#0b0e14', border:'1px solid #1e2636', color:'#dde9f0',
    padding:'11px 14px', fontFamily:'monospace', fontSize:13, marginBottom:10, boxSizing:'border-box' };
  const btnStyle   = (active) => ({ width:'100%', padding:'12px', fontFamily:'monospace', fontSize:11,
    letterSpacing:2, background: active ? '#0e9cc020' : '#1e2636', border:`1px solid ${active ? '#0e9cc0' : '#2a3a50'}`,
    color: active ? '#0e9cc0' : '#445566', cursor: active ? 'pointer' : 'wait', marginBottom:10 });

  if (step === 'pending') return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28, textAlign:'center' }}>
      <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
      <div style={{ fontFamily:'monospace', fontSize:13, color:'#10b981', marginBottom:8 }}>Request Submitted</div>
      <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', lineHeight:1.6 }}>
        Your phone number has been verified. The admin will review and approve your access.
      </div>
      <button onClick={onBack} style={{ marginTop:20, background:'none', border:'none', color:'#445566',
        fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1 }}>← BACK</button>
    </div>
  );

  return (
    <div style={{ background:'#0c0f18', border:'1px solid #1e2636', borderRadius:12, padding:28 }}>
      <div id="phone-recaptcha-phone" />
      <div style={{ fontFamily:'monospace', fontSize:11, color:'#0e9cc0', letterSpacing:2, marginBottom:20 }}>
        📱 PHONE OTP LOGIN
      </div>
      {step === 'phone' && (
        <>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 9876543210"
            style={inputStyle} />
          {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', background:'#f43f5e10', border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:12 }}>{err}</div>}
          <button onClick={sendOTP} disabled={busy} style={btnStyle(!busy)}>
            {busy ? 'SENDING…' : 'SEND OTP →'}
          </button>
        </>
      )}
      {step === 'otp' && (
        <>
          <div style={{ fontFamily:'monospace', fontSize:10, color:'#445566', marginBottom:12 }}>OTP sent to {phone}</div>
          <input value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit OTP"
            maxLength={6} style={inputStyle} />
          {err && <div style={{ fontFamily:'monospace', fontSize:10, color:'#f43f5e', background:'#f43f5e10', border:'1px solid #f43f5e33', padding:'8px 12px', marginBottom:12 }}>{err}</div>}
          <button onClick={verifyOTP} disabled={busy} style={btnStyle(!busy)}>
            {busy ? 'VERIFYING…' : 'VERIFY & SIGN IN →'}
          </button>
          <button type="button" onClick={() => { setStep('phone'); setOtp(''); setErr(''); }}
            style={{ background:'none', border:'none', color:'#445566', fontFamily:'monospace', fontSize:9, cursor:'pointer', letterSpacing:1, padding:0 }}>
            ← Change number
          </button>
        </>
      )}
      <button onClick={onBack}
        style={{ background:'none', border:'none', color:'#334455', fontFamily:'monospace',
          fontSize:9, cursor:'pointer', letterSpacing:1, padding:'12px 0 0', display:'block' }}>
        ← BACK
      </button>
    </div>
  );
}
